/**
 * Form Submission Utilities
 * Handles form submission with loading states, error handling, and button state management
 */

export class FormSubmissionHandler {
  constructor(form, options = {}) {
    this.form = form
    this.options = {
      submitUrl: options.submitUrl || form.action || window.location.pathname,
      method: options.method || form.method || 'POST',
      timeout: options.timeout || 30000,
      onSuccess: options.onSuccess || (() => {}),
      onError: options.onError || (() => {}),
      resetOnSuccess: options.resetOnSuccess !== false,
      ...options,
    }
    this.timeoutId = null
    this.submitButton = null
    this.originalButtonText = null
  }

  init() {
    if (!this.form) return

    // Remove any existing listeners to prevent duplicates
    // Use a named function so we can remove it later
    if (this.form._formSubmissionHandler) {
      this.form.removeEventListener('submit', this.form._formSubmissionHandler, true) // capture phase
      this.form.removeEventListener('submit', this.form._formSubmissionHandler, false) // bubble phase
    }

    // Create a bound handler that prevents default and calls our handler
    // Use capture phase (true) to run BEFORE Rails UJS
    this.form._formSubmissionHandler = (e) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation() // Prevent other handlers from running
      this.handleSubmit(e)
    }

    // Add listener in capture phase to run BEFORE Rails UJS
    this.form.addEventListener('submit', this.form._formSubmissionHandler, true)
  }

  handleSubmit(event) {
    // Prevent double submission
    if (this.form.dataset.submitting === 'true') {
      return false
    }
    this.form.dataset.submitting = 'true'

    const formData = new FormData(this.form)
    this.submitButton = this.form.querySelector(
      'input[type="submit"], button[type="submit"]'
    )

    // Get original button text - check data-disable-with attribute first
    // This handles cases where button might already be in a stuck state
    if (this.submitButton) {
      const defaultText =
        this.submitButton.getAttribute('data-disable-with') ||
        (this.submitButton.tagName === 'INPUT'
          ? this.submitButton.value
          : this.submitButton.textContent) ||
        'Submit'

      // If button is stuck in "Creating..." state, use the default from data-disable-with
      const currentText =
        this.submitButton.tagName === 'INPUT'
          ? this.submitButton.value
          : this.submitButton.textContent

    this.originalButtonText =
        currentText === 'Creating...' &&
        this.submitButton.getAttribute('data-disable-with')
          ? this.submitButton.getAttribute('data-disable-with')
          : currentText !== 'Creating...'
          ? currentText
          : defaultText
    } else {
      this.originalButtonText = 'Submit'
    }

    this.setLoadingState()
    this.setupTimeout()

    this.submitForm(formData)
  }

  setLoadingState() {
    if (this.submitButton) {
      if (this.submitButton.tagName === 'INPUT') {
        this.submitButton.value = 'Creating...'
      } else {
        this.submitButton.textContent = 'Creating...'
      }
      this.submitButton.disabled = true
    }
  }

  resetButtonState() {
    // Re-query button if reference is lost (e.g., if DOM was updated)
    if (!this.submitButton || !document.contains(this.submitButton)) {
      this.submitButton = this.form?.querySelector(
        'input[type="submit"], button[type="submit"]'
      )
    }

    if (this.submitButton) {
      if (this.submitButton.tagName === 'INPUT') {
        this.submitButton.value = this.originalButtonText
      } else {
        this.submitButton.textContent = this.originalButtonText
      }
      this.submitButton.disabled = false
    }
    if (this.form) {
      this.form.dataset.submitting = 'false'
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  setupTimeout() {
    this.timeoutId = setTimeout(() => {
      this.resetButtonState()
      this.showError('Request timed out. Please try again.')
    }, this.options.timeout)
  }

  async submitForm(formData) {
    try {
      const csrfToken = this.getCSRFToken()
      if (!csrfToken) {
        throw new Error('CSRF token not found. Please refresh the page and try again.')
      }

      const response = await fetch(this.options.submitUrl, {
        method: this.options.method,
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      })
      
      // Check if server returned HTML instead of JSON (might indicate redirect or error)
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('text/html') && !contentType.includes('application/json')) {
        // Server returned HTML - this shouldn't happen with Accept: application/json
        // But handle it gracefully
        console.warn('Server returned HTML instead of JSON. Response might be a redirect.')
        // Try to parse as JSON anyway, parseResponse will handle it
      }

      const data = await this.parseResponse(response)
      this.handleSuccess(data)
    } catch (error) {
      console.error('Form submission error:', error)
      this.handleError(error)
    } finally {
      // Always clear timeout
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
        this.timeoutId = null
      }
      // CRITICAL: Always clear the form submitting flag
      if (this.form) {
        this.form.dataset.submitting = 'false'
      }
      // Ensure button is always reset, even if something went wrong
      // This prevents the stuck "Creating..." state
      this.resetButtonState()
    }
  }

  async parseResponse(response) {
    const contentType = response.headers.get('content-type')
    const isJSON = contentType?.includes('application/json')

    // Handle non-JSON responses
    if (!isJSON) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication required. Please log in again.')
      }
      if (response.status === 422) {
        throw new Error('Validation error. Please check your input.')
      }
      if (response.ok) {
        return { success: true, nonJson: true }
      }
      const text = await response.text()
      throw new Error(`Server error (${response.status}). Please try again.`)
    }

    // Handle JSON error responses
    if (!response.ok) {
      try {
        const errorData = await response.json()
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        )
      } catch (parseError) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    }

    // Parse JSON success response
    try {
      return await response.json()
    } catch (parseError) {
      if (response.ok) {
        return { success: true, parseError: true }
      }
      throw new Error('Failed to parse server response.')
    }
  }

  handleSuccess(data) {
    // Reset button state FIRST before calling callbacks
    // This ensures button is reset even if callback throws an error
    this.resetButtonState()

    if (data.success || data.nonJson || data.parseError) {
      if (this.options.resetOnSuccess) {
        this.form.reset()
      }
      // Call onSuccess callback - if it throws, button is already reset
      try {
        this.options.onSuccess(data)
      } catch (callbackError) {
        // Button is already reset, so we're good
      }
    } else {
      throw new Error(data?.message || 'Failed to submit form')
    }
  }

  handleError(error) {
    // Always reset button state on error
    this.resetButtonState()

    // Log error for debugging
    console.error('FormSubmissionHandler error:', error)

    // Handle network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      this.showError(
        'Network error. Please check your connection and try again.'
      )
      this.options.onError(error)
      return
    }

    this.showError(error.message || 'An unexpected error occurred')
    this.options.onError(error)
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute('content') || ''
  }

  showError(message) {
    if (window.showError) {
      window.showError(message)
    } else {
      alert('Error: ' + message)
    }
  }

  showSuccess(message) {
    if (window.showSuccess) {
      window.showSuccess(message)
    } else {
      alert(message)
    }
  }
}
